import {Box, Link, Stack, Typography} from '@mui/material';
import React, {Fragment, useState} from 'react';

import {Bank as BankDTO} from 'domain/usecases';
import {BankValidityEnum} from 'domain/usecases/private/refund/request/get-validate-bank';
import {MyProfileEnum} from 'main/analytics/gaTagsEnum';
import {AlertInfo} from 'presentation/components';
import {FeaturesEnum, ProfileEnum} from 'presentation/constants';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {usePostBeneficiaryBankDataMutation} from 'presentation/pages/private/profile/queries/usePostBeneficiaryBankDataMutation';
import {usePutBeneficiaryBankDataMutation} from 'presentation/pages/private/profile/queries/usePutBeneficiaryBankDataMutation';
import {useValidateRefundBankMutation} from 'presentation/pages/private/profile/queries/useValidateRefundBankMutation';
import {BankAlert} from 'presentation/pages/private/refund/components/BankAlert';
import {translations} from 'presentation/translations-new';
import {useLocation, useNavigate} from 'react-router-dom';
import {EmptyAlert} from '../EmptyAlert';
import {EmptyAlertEnum} from '../EmptyAlert/constants';
import {ErrorAlert} from '../ErrorAlert';
import {ErrorLoadingData} from '../ErrorLoadingData';
import {SkeletonData} from '../SkeletonData';
import {SuccessAlert} from '../SuccessAlert';
import {BankData} from './BankData';
import {BankForm} from './BankForm';
import {StyledStack} from './styles';
import {BankWarning, DataAccordionBankProps} from './types';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useChangeDataProfile} from 'presentation/pages';

const {bank} = translations['pt-br'].privateFlow.profile.changeData;

const Bank = ({
	showForm,
	handleChangeForm,
	hiddenTitle = false,
	hiddenChangeAction,
	onEmptyAction,
	dataTagId,
	bankAccountWarning,
	isExternalLoading = false,
}: DataAccordionBankProps) => {
	const {isLoading, isError, data, refetch} = useGetBeneficiaryBankDataQuery();
	const [errorMessage, setErrorMessage] = useState('');
	const [submitWarning, setSubmitWarning] = useState<BankWarning | null>(null);
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const isProfileRoute = pathname === ProfileEnum.PROFILE;

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);
	const {isEnabled: isRefundBankAndAgencyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_BANK_AND_AGENCY,
	);

	const validateBankMutation = useValidateRefundBankMutation();

	const {
		mutateAsync: postBeneficiaryBankDataMutation,
		isPending: postBeneficiaryBankDataLoading,
		isError: postBeneficiaryBankDataError,
		isSuccess: postBeneficiaryBankDataSuccess,
	} = usePostBeneficiaryBankDataMutation({
		handleChangeForm,
		setErrorMessage,
	});

	const {
		mutateAsync: putBeneficiaryBankDataMutation,
		isPending: putBeneficiaryBankDataLoading,
		isError: putBeneficiaryBankDataError,
		isSuccess: putBeneficiaryBankDataSuccess,
	} = usePutBeneficiaryBankDataMutation({
		handleChangeForm,
		setErrorMessage,
	});

	const {onCancel} = useChangeDataProfile();

	const isSubmitSuccess = postBeneficiaryBankDataSuccess || putBeneficiaryBankDataSuccess;
	const isSubmitError = postBeneficiaryBankDataError || putBeneficiaryBankDataError;
	const isSubmitting =
		postBeneficiaryBankDataLoading ||
		putBeneficiaryBankDataLoading ||
		validateBankMutation.isPending;
	const showBankAlert = (data || showForm) && isProfileRoute;

	const handleNavToProfile = () => {
		navigate(ProfileEnum.PROFILE, {state: pathname});
	};

	if (isError) {
		return (
			<Stack gap={2} p={2}>
				<ErrorLoadingData resetErrorBoundary={refetch} />
			</Stack>
		);
	}

	if (isLoading || isExternalLoading) {
		return (
			<Stack gap={2} p={2}>
				<SkeletonData type="bank" />
			</Stack>
		);
	}

	const handleSave = async (bankData: BankDTO) => {
		if (isRefundBankAndAgencyEnabled) {
			const content = await validateBankMutation.mutateAsync({
				bankCode: bankData.bank.code,
				agencyCode: bankData.agency,
			});
			if (content?.isValid === BankValidityEnum.INVALID) {
				setSubmitWarning({title: bank.invalidBankAlertProfile.title});
				return undefined;
			}
			setSubmitWarning(null);
		}
		return data
			? putBeneficiaryBankDataMutation(bankData)
			: postBeneficiaryBankDataMutation(bankData);
	};

	const handleUpdateForm = () => {
		handleChangeForm();
	};

	const shouldShowNewProfileForm = isNewProfileFeatureFlagEnabled && isProfileRoute;
	const shouldShowLegacyForm = showForm && !isNewProfileFeatureFlagEnabled;
	const shouldShowEmptyState = !data && !shouldShowNewProfileForm && !shouldShowLegacyForm;
	const shouldShowBankData = data && !shouldShowNewProfileForm && !shouldShowLegacyForm;

	const warning = bankAccountWarning ?? submitWarning;

	return (
		<Fragment>
			<Box sx={{p: hiddenChangeAction ? 2 : ''}}>
				{warning ? (
					<AlertInfo.Root severity="error" sx={{mb: 2}}>
						<AlertInfo.Title title={warning.title} />
						{warning.description ? (
							<Typography variant="body2">{warning.description}</Typography>
						) : null}
					</AlertInfo.Root>
				) : null}
				{!hiddenTitle ? (
					<Typography variant="subtitle2" color="text.secondary">
						{bank.title}
					</Typography>
				) : null}

				<Stack gap={2}>
					{isSubmitSuccess ? <SuccessAlert type="bank" /> : null}
					{isSubmitError ? <ErrorAlert message={errorMessage} /> : null}

					{showBankAlert ? <BankAlert /> : null}
				</Stack>

				<Fragment>
					{shouldShowNewProfileForm ? (
						<BankForm
							data={data}
							onCancel={onCancel}
							onSave={handleSave}
							isSubmitting={isSubmitting}
							bankAccountFieldsInvalid={Boolean(submitWarning)}
						/>
					) : null}

					{shouldShowLegacyForm ? (
						<BankForm
							data={data}
							onCancel={handleUpdateForm}
							onSave={handleSave}
							isSubmitting={isSubmitting}
							bankAccountFieldsInvalid={Boolean(submitWarning)}
						/>
					) : null}

					{shouldShowEmptyState ? (
						<EmptyAlert
							hideAction={hiddenChangeAction}
							type={EmptyAlertEnum.BANK}
							onClick={onEmptyAction}
							dataTagId={MyProfileEnum.CLICK_CADASTRAR_DADOS_BANCARIOS}
						/>
					) : null}

					{shouldShowBankData ? (
						<BankData
							hiddenChangeAction={hiddenChangeAction}
							data={data}
							onChange={handleUpdateForm}
							isInvalid={Boolean(bankAccountWarning)}
						/>
					) : null}
				</Fragment>
			</Box>

			{hiddenChangeAction ? (
				<StyledStack data-tag-id={dataTagId}>
					<Typography variant="caption" color="text.secondary">
						{bank.goToPofileMessage}
					</Typography>
					<Link
						onClick={handleNavToProfile}
						variant="body2"
						color="primary.main"
						sx={{cursor: 'pointer', fontSize: 12}}>
						{bank.goToProfile}
					</Link>
				</StyledStack>
			) : null}
		</Fragment>
	);
};

export {Bank};
